import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { useAuth } from '../../context/AuthContext.js';
import isExternalNetworkEnabled from '../../utils/networkMode.js';
import { useTranslation } from 'react-i18next';
import {
  AnalyticsTable,
  analyticsStatsGrid4Class,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { formatCurrencyWhole as fmt } from '../../utils/formatCurrency.js';
import {
  ArrowLeft,
  Network,
  Users,
  Package,
  Handshake,
  MapPin,
} from 'lucide-react';

const AgentNetworkAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const {
    companies,
    selectedCompany,
    handleCompanyChange,
    currentUser,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates });

  useEffect(() => {
    if (ready) fetchAnalytics();
  }, [filter, selectedCompany, ready]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser: currentUser || user,
      });
      const result = await analyticsService.getAgentNetworkAnalytics(params);
      setData(result);
    } catch (err) {
      const status = err.response?.status;
      const apiError = err.response?.data?.error || '';
      if (status === 404 || String(apiError).includes('404 Not Found')) {
        Alert.error(
          'Agent network API is unavailable. Stop duplicate Flask processes on port 5000, run python start_server.py once, then refresh.'
        );
      } else {
        Alert.error(apiError || 'Failed to load agent network analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout fullWidth>
        <Loader fullScreen={false} message="Loading Agent Network Analytics..." />
      </DashboardLayout>
    );
  }

  const summary = data?.summary || {};
  const enabled = data?.enabled !== false;

  return (
    <DashboardLayout fullWidth>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/analytics')}
              className="p-2 hover:bg-gray-100 rounded-xl"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-secondary flex items-center gap-2">
                <Network className="w-5 h-5" /> Agent Network Analytics
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Full report — partnerships, external shipments by city and agent
              </p>
              {enabled && data?.filter && (
                <p className="text-xs text-violet-700 mt-1">
                  {data.filter.start_date} → {data.filter.end_date}
                  {summary.external_companies != null && summary.external_companies > 0
                    ? ` · ${summary.external_companies} external ${summary.external_companies === 1 ? 'company' : 'companies'}`
                    : ''}
                  {data.network_mode ? ` · ${data.network_mode}` : ''}
                </p>
              )}
            </div>
          </div>
          <DateFilterDropdown
            filter={filter}
            setFilter={setFilter}
            customDates={customDates}
            setCustomDates={setCustomDates}
            onApply={fetchAnalytics}
            currentUser={currentUser || user}
            companies={companies}
            selectedCompany={selectedCompany}
            onCompanyChange={handleCompanyChange}
            showCompanyFilter
          />
        </div>

        {!enabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-sm text-amber-900">
              {data?.message ||
                'Agent Network is not enabled for this company. Set Operation Mode to External and enable Agent Network under company settings.'}
            </p>
          </div>
        )}

        {enabled && (
          <>
            <div className="mb-2">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Period summary
              </h2>
            </div>
            <div className={`${analyticsStatsGrid4Class} mb-6`}>
              <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 border-l-4 border-blue-200 min-w-0">
                <p className="text-xs sm:text-sm text-gray-500">Active partnerships</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800">{summary.partnerships_accepted ?? 0}</p>
                <p className="text-xs text-gray-400">
                  Pending: {summary.partnerships_pending ?? 0} · Revoked: {summary.partnerships_revoked ?? 0}
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 border-l-4 border-blue-200 min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                  <Users className="w-4 h-4" /> Partner agents
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800">{summary.unique_partner_agents ?? 0}</p>
                <p className="text-xs text-gray-400">
                  {summary.partnerships_total ?? 0} total partnership requests
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 border-l-4 border-blue-200 min-w-0">
                <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                  <Package className="w-4 h-4" /> External shipments
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800">{summary.external_shipments ?? 0}</p>
                <p className="text-xs text-gray-400">
                  {fmt(summary.external_shipment_freight)} freight
                  {summary.internal_receiver_assignments > 0
                    ? ` · ${summary.internal_receiver_assignments} internal assignments`
                    : ''}
                </p>
              </div>
            </div>

            <div className="mb-2">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Breakdown
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-md p-4">
                <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Handshake className="w-4 h-4" /> Partnership overview
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total requests</span>
                    <span className="font-semibold">{summary.partnerships_total ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Accepted</span>
                    <span className="font-semibold text-green-600">{summary.partnerships_accepted ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending</span>
                    <span className="font-semibold text-amber-600">{summary.partnerships_pending ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revoked</span>
                    <span className="font-semibold text-red-600">{summary.partnerships_revoked ?? 0}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">Internal receiver assignments</span>
                    <span className="font-semibold">{summary.internal_receiver_assignments ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-2 mt-2">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                By city &amp; partner agent
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <AnalyticsTable
                title={<span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> By service city</span>}
                columns={[
                  { header: 'City', align: 'left' },
                  { header: 'Partners', align: 'right' },
                  { header: 'Ext. shipments', align: 'right' },
                ]}
                rows={(data?.by_service_city || []).map((row) => ({
                  key: row.service_city,
                  cells: [row.service_city, row.partnerships_accepted, row.external_shipments],
                }))}
                emptyMessage="No data for this period"
                tableMinWidth="420px"
              />

              <AnalyticsTable
                title={<span className="flex items-center gap-2"><Users className="w-4 h-4" /> Top partner agents</span>}
                columns={[
                  { header: 'Agent', align: 'left' },
                  { header: 'Home company', align: 'left' },
                  { header: t('pages.shipment.title'), align: 'right' },
                ]}
                rows={(data?.top_partner_agents || []).map((row) => ({
                  key: row.agent_user_id,
                  cells: [
                    <div key="agent">
                      <div className="font-medium">{row.agent_name}</div>
                      <div className="text-xs text-gray-400">{(row.service_cities || []).join(', ')}</div>
                    </div>,
                    row.home_company_name || '—',
                    row.external_shipments,
                  ],
                }))}
                emptyMessage="No external receiver assignments in period"
                tableMinWidth="480px"
              />
            </div>
          </>
        )}

        {(user?.role === 'System Admin' || isExternalNetworkEnabled(user)) && enabled && (
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/partner-agents')}
              className="px-4 py-2 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg text-sm hover:bg-sky-100"
            >
              Manage partnerships
            </button>
            <button
              type="button"
              onClick={() => navigate('/partner-agents/find')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Find agent
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AgentNetworkAnalytics;
